import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Button,
  Flex,
  Avatar,
  Tag,
  Alert,
  Spin,
} from 'antd';
import {
  DownloadOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  RiseOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CarOutlined,
  ThunderboltOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useResponsive';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/TranslationContext';
import dashboardService from '../../service';

const { Text, Title } = Typography;

const StatCard = ({ label, value, description, icon, color }) => (
  <Card>
    <Flex justify="space-between" align="flex-start" style={{ marginBottom: 8 }}>
      <div>
        <Text type="secondary" style={{ fontSize: '0.875rem' }}>
          {label}
        </Text>
        <Title level={4} style={{ margin: '4px 0' }}>
          {value}
        </Title>
      </div>
      <Avatar
        style={{
          backgroundColor: color || 'var(--ant-color-primary)',
          color: 'white',
        }}
        icon={icon}
      />
    </Flex>
    <Text type="secondary" style={{ fontSize: '0.875rem' }}>
      {description}
    </Text>
  </Card>
);

function DashboardMain() {
  const { user, isSuperAdmin, isManager, isDealer, isSubdealer } = useAuth();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [dashboardData, setDashboardData] = useState(null);
  const [branchStats, setBranchStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatCurrency = (value) => {
    const numValue = Number(value) || 0;
    if (numValue >= 1000) {
      return `$${(numValue / 1000).toFixed(1)}K`;
    }
    return `$${numValue.toFixed(0)}`;
  };

  useEffect(() => {
    if (user) {
      loadDashboard();
      loadBranchStats();
    }
  }, [user]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      let data;

      if (user.user_id) {
        data = await dashboardService.getDashboard(
          user.role,
          user.user_id,
          user.managerId,
          user.dealerId,
          user.branchId
        );
      } else {
        if (isSuperAdmin) {
          data = await dashboardService.getSuperAdminDashboard();
        } else if (isManager) {
          data = await dashboardService.getManagerDashboard(user.managerId, user.branchId);
        } else if (isDealer) {
          data = await dashboardService.getDealerDashboard(user.dealerId, user.user_id);
        } else if (isSubdealer) {
          data = await dashboardService.getSubdealerDashboard(user.user_id);
        }
      }

      if (data?.success) {
        setDashboardData(data.dashboard);
      }
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load dashboard data. Using cached user information.');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadBranchStats = async () => {
    try {
      const data = await dashboardService.getDealersByBranch();
      if (data.success) {
        setBranchStats(data);
      }
    } catch (err) {
      console.error('Failed to load branch stats:', err);
    }
  };

  const handleRefresh = () => {
    loadDashboard();
    loadBranchStats();
  };

  const getWelcomeMessage = () => {
    switch (user?.role) {
      case 'super_admin':
        return t('dashboard.systemAdminDashboard');
      case 'manager':
        return t('dashboard.branchManagerDashboard');
      case 'dealer':
        return t('dashboard.dealerDashboard');
      case 'subdealer':
        return t('dashboard.yourAuctionDashboard');
      default:
        return t('dashboard.welcomeToDashboard');
    }
  };

  const getOverviewType = () => {
    switch (user?.role) {
      case 'super_admin':
        return t('dashboard.systemOverview');
      case 'manager':
        return t('dashboard.branchOverview');
      default:
        return t('dashboard.activityOverview');
    }
  };

  const stats = dashboardData?.overview || user?.stats || {};

  if (branchStats?.totals && user?.role === 'super_admin') {
    stats.totalBranches = branchStats.totals.totalBranches || stats.totalBranches;
    stats.totalManagers = branchStats.totals.totalManagers || stats.totalManagers;
    stats.totalDealers = branchStats.totals.totalDealers || stats.totalDealers;
    stats.totalSubdealers = branchStats.totals.totalSubdealers || stats.totalSubdealers;
  }

  if (loading && !dashboardData) {
    return (
      <Flex justify="center" align="center" style={{ height: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <div>
      {error && (
        <Alert
          message={error}
          type="warning"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      <Flex
        justify="space-between"
        align={isMobile ? 'flex-start' : 'center'}
        gap={16}
        wrap="wrap"
        style={{ marginBottom: isMobile ? 24 : 32 }}
      >
        <div>
          <Title level={isMobile ? 4 : 3} style={{ marginBottom: 4 }}>
            {getWelcomeMessage()}
          </Title>
          <Text type="secondary" style={{ fontSize: '0.875rem' }}>
            {t('dashboard.welcomeBack')}, {user?.name}! {t('dashboard.hereIsYour')} {getOverviewType()} {t('dashboard.overview')}.
          </Text>
        </div>
        <Flex align="center" gap={8}>
          <Tag color={user?.role === 'super_admin' ? 'red' : user?.role === 'manager' ? 'blue' : 'default'}>
            {user?.role?.replace('_', ' ').toUpperCase()}
          </Tag>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            style={{ minWidth: 44, minHeight: 44 }}
          />
        </Flex>
      </Flex>

      {/* Key Actions */}
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} md={8}>
          <Card
            style={{
              height: '100%',
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #172d4a 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
            }}
            styles={{ body: { position: 'relative', zIndex: 1 } }}
          >
            <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
              <Avatar
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                icon={<DownloadOutlined />}
              />
              <Text strong style={{ color: 'white', fontSize: 16 }}>
                {t('dashboard.downloadApp')}
              </Text>
            </Flex>
            <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: 24, display: 'block', fontSize: '0.875rem' }}>
              {t('dashboard.downloadAppDesc')}
            </Text>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              block={isMobile}
              href="https://example.com/your-app-download"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: 'none',
              }}
            >
              {t('dashboard.downloadNow')}
            </Button>
            <div
              style={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
              }}
            />
          </Card>
        </Col>

        {(user?.role === 'dealer' || user?.role === 'subdealer') && (
          <>
            <Col xs={24} sm={12} md={8}>
              <Card
                style={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                styles={{ body: { position: 'relative', zIndex: 1 } }}
              >
                <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
                  <Avatar
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                    icon={<UserOutlined />}
                  />
                  <Text strong style={{ color: 'white', fontSize: 16 }}>
                    {t('dashboard.yourManager')}
                  </Text>
                </Flex>
                <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: 24, display: 'block', fontSize: '0.875rem' }}>
                  {t('dashboard.yourManagerDesc')}
                </Text>
                <Flex gap={8} wrap="wrap">
                  <Button
                    type="primary"
                    size={isMobile ? 'middle' : 'small'}
                    block={isMobile}
                    href="https://wa.me/PLACEHOLDER_WHATSAPP"
                    target="_blank"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: 'none',
                    }}
                  >
                    {t('dashboard.whatsapp')}
                  </Button>
                  <Button
                    type="primary"
                    size={isMobile ? 'middle' : 'small'}
                    block={isMobile}
                    href="viber://chat?number=PLACEHOLDER_VIBER"
                    target="_blank"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: 'none',
                    }}
                  >
                    {t('dashboard.viber')}
                  </Button>
                </Flex>
                <div
                  style={{
                    position: 'absolute',
                    bottom: -50,
                    left: -50,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card
                style={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                styles={{ body: { position: 'relative', zIndex: 1 } }}
              >
                <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
                  <Avatar
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                    icon={<RiseOutlined />}
                  />
                  <Text strong style={{ color: 'white', fontSize: 16 }}>
                    {t('dashboard.tarifPlan')}
                  </Text>
                </Flex>
                <Title level={4} style={{ color: 'white', marginBottom: 8 }}>
                  {t('dashboard.basic')}
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.9)', marginBottom: 16, display: 'block', fontSize: '0.875rem' }}>
                  {t('dashboard.planLimits')}
                </Text>
                <Button
                  type="primary"
                  icon={<RightOutlined />}
                  block={isMobile}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: 'none',
                  }}
                >
                  {t('dashboard.upgradePlan')}
                </Button>
                <div
                  style={{
                    position: 'absolute',
                    top: -30,
                    right: -30,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                  }}
                />
              </Card>
            </Col>
          </>
        )}
      </Row>

      {/* Statistics */}
      <Title level={5} style={{ marginBottom: 24 }}>
        {user?.role === 'super_admin'
          ? t('dashboard.systemMetrics')
          : user?.role === 'manager'
          ? t('dashboard.branchPerformance')
          : t('dashboard.yourActivity')}
      </Title>

      <Row gutter={[16, 16]}>
        {user?.role === 'super_admin' && (
          <>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('dashboard.totalBranches')}
                value={stats.totalBranches || user?.stats?.totalBranches || 0}
                description={t('dashboard.activeBranchesWorldwide')}
                icon={<BankOutlined />}
                color="var(--ant-color-primary)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('managers.totalManagers')}
                value={stats.totalManagers || user?.stats?.totalManagers || 0}
                description={t('dashboard.branchManagers')}
                icon={<UserOutlined />}
                color="var(--ant-color-info)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('dealers.totalDealers')}
                value={stats.totalDealers || user?.stats?.totalDealers || 0}
                description={t('dashboard.acrossAllBranches')}
                icon={<TeamOutlined />}
                color="var(--ant-color-info)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('branches.totalSubdealers')}
                value={stats.totalSubdealers || user?.stats?.totalSubdealers || 0}
                description={t('dashboard.acrossAllBranches')}
                icon={<TeamOutlined />}
                color="var(--ant-color-primary)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('dashboard.totalRevenue')}
                value={formatCurrency(stats.totalRevenue || user?.stats?.totalRevenue || 0)}
                description={t('dashboard.totalPlatformRevenue')}
                icon={<RiseOutlined />}
                color="var(--ant-color-warning)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label="Win Cars"
                value={stats.totalWinCars || user?.stats?.totalWinCars || 0}
                description="Total Cars Won"
                icon={<CarOutlined />}
                color="var(--ant-color-success)"
              />
            </Col>
          </>
        )}

        {user?.role === 'manager' && (
          <>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('dashboard.mySubdealers')}
                value={stats.totalSubdealers || user?.stats?.totalSubdealers || 0}
                description={t('dashboard.activeInYourBranch')}
                icon={<TeamOutlined />}
                color="var(--ant-color-primary)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('subdealers.activeBids')}
                value={stats.activeBids || user?.stats?.activeBids || 0}
                description={t('dashboard.currentlyInProgress')}
                icon={<RiseOutlined />}
                color="var(--ant-color-primary)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('bids.totalBids')}
                value={stats.totalBids || user?.stats?.totalBids || 0}
                description={t('dashboard.byYourBranch')}
                icon={<CheckCircleOutlined />}
                color="var(--ant-color-success)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label="Win Cars"
                value={stats.totalWinCars || user?.stats?.totalWinCars || 0}
                description="Branch Cars Won"
                icon={<CarOutlined />}
                color="var(--ant-color-success)"
              />
            </Col>
          </>
        )}

        {(user?.role === 'dealer' || user?.role === 'subdealer') && (
          <>
            {user?.role === 'dealer' && (
              <Col xs={24} sm={12} md={8} lg={6}>
                <StatCard
                  label={t('dealers.totalSubdealers')}
                  value={stats.totalSubdealers || user?.stats?.totalSubdealers || 0}
                  description={t('dashboard.activeInYourBranch')}
                  icon={<TeamOutlined />}
                  color="var(--ant-color-primary)"
                />
              </Col>
            )}
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('bids.totalBids')}
                value={stats.totalBids || stats.dealerBids || user?.stats?.totalBids || 0}
                description={t('dashboard.totalBidsPlaced')}
                icon={<ThunderboltOutlined />}
                color="var(--ant-color-info)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('subdealers.activeBids')}
                value={stats.activeBids || user?.stats?.activeBids || 0}
                description={t('dashboard.currentlyInProgress')}
                icon={<RiseOutlined />}
                color="var(--ant-color-primary)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('subdealers.wonAuctions')}
                value={stats.wonAuctions || user?.stats?.wonAuctions || 0}
                description={t('dashboard.totalWonAuctions')}
                icon={<CheckCircleOutlined />}
                color="var(--ant-color-success)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label={t('dealers.successRate')}
                value={
                  stats.successRate !== undefined
                    ? `${stats.successRate.toFixed(1)}%`
                    : user?.stats?.successRate
                    ? `${user.stats.successRate}%`
                    : '0%'
                }
                description={t('dealers.successRate')}
                icon={<RiseOutlined />}
                color="var(--ant-color-warning)"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                label="Win Cars"
                value={stats.totalWinCars || user?.stats?.totalWinCars || 0}
                description="Total Cars Won"
                icon={<CarOutlined />}
                color="var(--ant-color-success)"
              />
            </Col>
          </>
        )}
      </Row>
    </div>
  );
}

export default DashboardMain;
